import timezoneData from '@/data/predefined/timezone.json'
import browserData from '@/data/predefined/browser.json'

// Cache timezone data as object with value as key
const timezoneCache = timezoneData.reduce((acc, item) => {
  acc[item.value] = item.label
  return acc
}, {})

// Cache browser data as object with value as key
const browserCache = browserData.reduce((acc, item) => {
  acc[item.value] = item.label
  return acc
}, {})

export const getLocaleTimezone = (value, l = 'en', empty = '-') => {
  if (!value || !l) return empty
  const locale = l.split('-')[0]
  const tz = timezoneCache[value]
  if (tz) {
    return tz[locale] || tz.en || tz.zh || tz.km || tz.vi || empty
  }
  return empty
}

export const getLocaleBrowser = (value, l = 'en', empty = '-') => {
  if (!value || !l) return empty
  const locale = l.split('-')[0]
  const browser = browserCache[value]
  if (browser) {
    return browser[locale] || browser.en || browser.zh || browser.km || browser.vi || empty
  }
  return empty
}

export function applyKhmerLocalePatch() {
  console.log('applyKhmerLocalePatch')

  // Store the original constructor
  const OriginalDateTimeFormat = Intl.DateTimeFormat

  // Khmer month names
  const khmerMonths = [
    'មករា',
    'កុម្ភៈ',
    'មីនា',
    'មេសា',
    'ឧសភា',
    'មិថុនា',
    'កក្កដា',
    'សីហា',
    'កញ្ញា',
    'តុលា',
    'វិច្ឆិកា',
    'ធ្នូ',
  ]

  // Khmer weekday names (short and long versions)
  const khmerWeekdays = {
    short: ['អា', 'ច', 'អ', 'ព', 'ព្រ', 'សុ', 'ស'],
    long: ['អាទិត្យ', 'ចន្ទ', 'អង្គារ', 'ពុធ', 'ព្រហស្បតិ៍', 'សុក្រ', 'សៅរ៍'],
  }

  function CustomDateTimeFormat(locales, options) {
    const requested = Array.isArray(locales) ? locales[0] : locales
    const normalized = typeof requested === 'string' ? requested.replace('_', '-').toLowerCase() : ''
    const isKhmer = normalized === 'km' || normalized === 'km-kh' || normalized === 'kh'

    const dtf = new OriginalDateTimeFormat(locales, options)

    if (isKhmer) {
      return new Proxy(dtf, {
        get(target, prop, receiver) {
          if (prop === 'resolvedOptions') {
            return () => {
              const opts = target.resolvedOptions ? target.resolvedOptions() : {}
              return { ...opts, locale: 'km-KH' }
            }
          }
          if (prop === 'format') {
            return date => {
              // Build from parts to ensure full string preserved
              const parts = (typeof target.formatToParts === 'function')
                ? target.formatToParts(date)
                : [{ type: 'literal', value: target.format(date) }]

              const dateObj = date instanceof Date ? date : new Date(date)
              const monthIndex = dateObj.getMonth()
              const dayIndex = dateObj.getDay()
              const useLongWeekday = options && options.weekday === 'long'

              return parts
                .map(p => {
                  if (p.type === 'month') {
                    return khmerMonths[monthIndex]
                  }
                  if (p.type === 'weekday') {
                    return (useLongWeekday ? khmerWeekdays.long : khmerWeekdays.short)[dayIndex]
                  }
                  return p.value
                })
                .join('')
            }
          }

          if (prop === 'formatToParts') {
            return date => {
              const parts = target.formatToParts(date)
              const dateObj = date instanceof Date ? date : new Date(date)
              const monthIndex = dateObj.getMonth()
              const dayIndex = dateObj.getDay()
              return parts.map(p => {
                if (p.type === 'month') {
                  return { ...p, value: khmerMonths[monthIndex] }
                }
                if (p.type === 'weekday') {
                  // Prefer long if requested, else short
                  const useLong = options && options.weekday === 'long'
                  return { ...p, value: (useLong ? khmerWeekdays.long : khmerWeekdays.short)[dayIndex] }
                }
                return p
              })
            }
          }

          const value = Reflect.get(target, prop, receiver)
          if (typeof value === 'function') {
            return value.bind(target)
          }
          return value
        },
      })
    }

    return dtf
  }

  // Copy static properties from OriginalDateTimeFormat to CustomDateTimeFormat
  Object.getOwnPropertyNames(OriginalDateTimeFormat).forEach(key => {
    try {
      CustomDateTimeFormat[key] = OriginalDateTimeFormat[key]
    } catch (e) {
      //
    }
  })

  // Ensure prototype chain compatibility
  try {
    CustomDateTimeFormat.prototype = OriginalDateTimeFormat.prototype
  } catch (e) {
    //
  }

  // Override supportedLocalesOf to include km/km-KH as supported
  // eslint-disable-next-line func-names
  CustomDateTimeFormat.supportedLocalesOf = function (locales, options) {
    const input = Array.isArray(locales) ? locales : [locales]
    const normalized = input
      .filter(Boolean)
      .map(l => String(l).replace('_', '-'))

    const base = OriginalDateTimeFormat.supportedLocalesOf(normalized, options)

    const extra = normalized.filter(l => {
      const ll = l.toLowerCase()
      return ll === 'km' || ll === 'km-kh' || ll === 'kh'
    })

    // Merge unique locales
    const set = new Set([...base, ...extra])
    return Array.from(set)
  }

  // Replace the global Intl.DateTimeFormat
  Intl.DateTimeFormat = CustomDateTimeFormat
}
